/**
 * Typeahead search over the bQuest provider directory, by company name.
 *
 * Separate from useServiceProviderSearch in this same folder, which looks the same and is not: that
 * one searches professionals (SP and FA users) and returns user ids. This returns service provider
 * ids, which is what a record pointing at a provider profile needs.
 */

import { useEffect, useState } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { listProviders } from '../services/adminProvidersApi';

/** Below this the directory returns almost everything, which is not a search result. */
const MIN_QUERY_LENGTH = 2;

/** A typeahead, not a browse: enough rows to recognise the right one without becoming a list. */
const MAX_RESULTS = 8;

const DEBOUNCE_MS = 300;

export type DirectoryMatch = {
  serviceProviderId: string;
  name: string;
};

const useDebouncedValue = (value: string, delayMs: number): string => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
};

/**
 * ponytail: reuses GET /admin/providers, which selects whole provider rows and runs a total count
 * this search has no use for — it wants eight {id, name} pairs. Ceiling: a wide query and a count
 * per debounced term. Tolerable because the Care Concierge team is a handful of people editing a
 * case now and then, and React Query holds each term for a minute. Upgrade path: a lean
 * /admin/providers/search returning id and companyName with no count, the way
 * /admin/resources/author-pro/search already does for professionals — worth it the day this lands
 * on a surface with real traffic.
 */
export const useProviderDirectorySearch = (
  term: string,
): UseQueryResult<DirectoryMatch[], Error> & {
  isSearchable: boolean;
  isDebouncing: boolean;
} => {
  const trimmed = term.trim();
  const debounced = useDebouncedValue(trimmed, DEBOUNCE_MS);
  const isSearchable = debounced.length >= MIN_QUERY_LENGTH;

  /**
   * Exposed because "no search is running" and "no search has run yet" are not the same thing, and
   * only this hook can tell them apart. During the debounce the query has not been enabled, so
   * every flag it offers reads exactly as it does after a search that found nobody — which is the
   * one moment a caller must not treat as an answer from the directory.
   */
  const isDebouncing = debounced !== trimmed;

  const query = useQuery({
    queryKey: ['admin', 'provider-directory-search', debounced] as const,
    queryFn: async () => {
      const page = await listProviders({ search: debounced, pageSize: MAX_RESULTS });
      return page.items.map((provider) => ({
        serviceProviderId: provider.id,
        name: provider.companyName,
      }));
    },
    enabled: isSearchable,
    staleTime: 60_000,
  });

  return Object.assign(query, { isSearchable, isDebouncing });
};
